import type {
  ActionApi,
  ArchivePreviewApi,
  AttachmentApi,
  ChangeApi,
  ChecksApi,
  DiffItemApi,
  FieldDiffApi,
  NodeApi,
  NodeDetailApi,
  ReleaseApi,
  ResolvedApi,
  SectionApi,
  UploadApi,
  UploadRowApi,
  ValueApi,
  WriteEnvelopeApi,
} from '../types/taxonomy-studio.api.types';
import type {
  ArchivePreview,
  Attachment,
  Change,
  DiffItem,
  DraftAction,
  DraftChecks,
  FieldDiff,
  ItemAudit,
  NodeDetail,
  Release,
  ResolvedNode,
  Section,
  TaxonomyNode,
  TaxonomyValue,
  Upload,
  UploadRow,
  WriteResult,
} from '../types/taxonomy-studio.types';

/** Revision audit members shared by nodes, attributes, values and attachments. */
function toAudit(w: {
  created_by?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
  last_changed_seq?: number | null;
}): ItemAudit {
  return {
    createdBy: w.created_by ?? null,
    updatedBy: w.updated_by ?? null,
    updatedAt: w.updated_at ?? null,
    lastChangedSeq: w.last_changed_seq ?? null,
  };
}

/** Snake → camel for a release; tolerates a missing summary (older rows, imports). */
export function toRelease(w: ReleaseApi): Release {
  return {
    seq: w.seq,
    name: w.name,
    kind: w.kind,
    status: w.status,
    restoresSeq: w.restores_seq,
    sourceLabel: w.source_label,
    notes: w.notes,
    summary: {
      total: w.summary?.total ?? w.change_count,
      byType: w.summary?.by_type ?? {},
      byOp: w.summary?.by_op ?? {},
    },
    changeCount: w.change_count,
    draftVersion: w.draft_version,
    contributors: w.contributors ?? [],
    createdAt: w.created_at,
    createdBy: w.created_by,
    publishedAt: w.published_at,
    publishedBy: w.published_by,
    closedAt: w.closed_at,
    closedBy: w.closed_by,
    updatedBy: w.updated_by ?? null,
    updatedAt: w.updated_at ?? null,
  };
}

/** Tree row (also used for `q` search rows, which add `path_text`). */
export function toNode(w: NodeApi): TaxonomyNode {
  return {
    id: w.id,
    name: w.name,
    parentId: w.parent_id,
    depth: w.depth,
    isLeaf: w.is_leaf,
    childCount: w.child_count,
    ownAttributeCount: w.own_attribute_count,
    status: w.status,
    origin: w.origin,
    changedInDraft: w.changed_in_draft,
    pathText: w.path_text ?? null,
    audit: toAudit(w),
  };
}

export function toNodeDetail(w: NodeDetailApi): NodeDetail {
  return {
    ...toNode(w),
    path: w.path,
    pathText: w.path_text,
    verticalId: w.vertical_id,
    revisionId: w.revision_id,
  };
}

export function toResolved(w: ResolvedApi): ResolvedNode {
  return {
    node: {
      id: w.node.id,
      name: w.node.name,
      path: w.node.path,
      pathIds: w.node.path_ids,
      status: w.node.status,
    },
    attributeCount: w.attribute_count,
    attributes: w.attributes.map((a) => ({
      key: a.key,
      name: a.name,
      valueType: a.value_type,
      units: a.units,
      definition: a.definition,
      attachedAt: {
        nodeId: a.attached_at.node_id,
        name: a.attached_at.name,
        distance: a.attached_at.distance,
      },
      alsoAttachedAt: a.also_attached_at.map((x) => ({
        nodeId: x.node_id,
        name: x.name,
        distance: x.distance,
      })),
      confidence: a.confidence,
      provisional: a.provisional,
      isKey: a.is_key,
      gmc: a.gmc,
      values: a.values ?? [],
      valuesTotal: a.values_total,
      valuesAllowedHere: a.values_allowed_here,
      changedInDraft: a.changed_in_draft ?? null,
    })),
  };
}

export function toSection(w: SectionApi): Section {
  return {
    key: w.key,
    name: w.name,
    labelEnGb: w.label_en_gb,
    labels: w.labels ?? {},
    valueType: w.value_type,
    units: w.units,
    definition: w.definition,
    origin: w.origin,
    valueCount: w.value_count,
    attachmentCount: w.attachment_count,
    gmc: w.gmc.map((g) => ({
      field: g.field,
      relation: g.relation,
      declaredBy: g.declared_by ?? [],
      note: g.note ?? '',
    })),
    status: w.status,
    revisionId: w.revision_id,
    changedInDraft: w.changed_in_draft,
    lastChangedSeq: w.last_changed_seq,
    audit: toAudit(w),
  };
}

export function toValue(w: ValueApi): TaxonomyValue {
  return {
    key: w.key,
    sectionKey: w.section_key,
    name: w.name,
    synonyms: w.synonyms,
    labelEnGb: w.label_en_gb,
    scope: w.scope
      ? {
          nodeIds: w.scope.node_ids,
          nodes: (w.scope.nodes ?? []).map((n) => ({
            id: n.id,
            name: n.name,
            pathText: n.path_text,
          })),
        }
      : null,
    status: w.status,
    revisionId: w.revision_id,
    changedInDraft: w.changed_in_draft,
    audit: toAudit(w),
  };
}

export function toAttachment(w: AttachmentApi): Attachment {
  return {
    sectionKey: w.section_key,
    sectionName: w.section_name,
    nodeId: w.node_id,
    nodeName: w.node_name,
    nodePath: w.node_path,
    nodeStatus: w.node_status,
    confidence: w.confidence,
    provisional: w.provisional,
    basis: w.basis,
    rationale: w.rationale,
    status: w.status,
    revisionId: w.revision_id,
    changedInDraft: w.changed_in_draft,
    audit: toAudit(w),
  };
}

function toFieldDiff(w: FieldDiffApi): FieldDiff {
  return { field: w.field, label: w.label ?? w.field, before: w.before, after: w.after };
}

export function toChange(w: ChangeApi): Change {
  return {
    id: w.id,
    releaseSeq: w.release_seq,
    entityType: w.entity_type,
    entityKey: w.entity_key,
    entityLabel: w.entity_label,
    op: w.op,
    diff: w.diff.map(toFieldDiff),
    reason: w.reason,
    source: w.source,
    uploadId: w.upload_id,
    editCount: w.edit_count,
    createdAt: w.created_at,
    createdBy: w.created_by,
    updatedAt: w.updated_at,
    updatedBy: w.updated_by,
  };
}

export function toDiffItem(w: DiffItemApi): DiffItem {
  return {
    entityType: w.entity_type,
    entityKey: w.entity_key,
    entityLabel: w.entity_label,
    op: w.op,
    before: w.before,
    after: w.after,
    diff: w.diff.map(toFieldDiff),
  };
}

export function toAction(w: ActionApi): DraftAction {
  return {
    id: w.id,
    kind: w.kind,
    label: w.label,
    itemCount: w.item_count,
    createdAt: w.created_at,
    createdBy: w.created_by,
    undoneAt: w.undone_at,
    canUndo: w.can_undo,
  };
}

export function toChecks(w: ChecksApi): DraftChecks {
  const issue = (i: ChecksApi['errors'][number]) => ({
    code: i.code,
    message: i.message,
    target: i.target,
    count: i.count ?? null,
  });
  return {
    draftVersion: w.draft_version,
    ready: w.ready,
    errors: w.errors.map(issue),
    warnings: w.warnings.map(issue),
    info: w.info.map(issue),
  };
}

export function toArchivePreview(w: ArchivePreviewApi): ArchivePreview {
  return {
    nodes: w.nodes,
    nodeIds: w.node_ids,
    attachments: w.attachments,
    scopedValuesEmptied: w.scoped_values_emptied.map((v) => ({
      sectionKey: v.section_key,
      valueKey: v.value_key,
      name: v.name,
    })),
  };
}

export function toUpload(w: UploadApi): Upload {
  return {
    id: w.id,
    template: w.template,
    fileName: w.file_name,
    status: w.status,
    releaseSeq: w.release_seq,
    rowCount: w.row_count,
    counts: w.counts,
    errors: w.errors,
    stale: w.stale,
    uploadedAt: w.uploaded_at,
    uploadedBy: w.uploaded_by,
    appliedAt: w.applied_at,
    appliedBy: w.applied_by,
    updatedBy: w.updated_by ?? null,
    updatedAt: w.updated_at ?? null,
  };
}

export function toUploadRow(w: UploadRowApi): UploadRow {
  return {
    rowNo: w.row_no,
    cells: w.cells,
    outcome: w.outcome,
    entityKey: w.entity_key,
    message: w.message,
    errorColumn: w.error_column,
    diff: w.diff.map(toFieldDiff),
    edited: w.edited,
    editedBy: w.edited_by ?? null,
    editedAt: w.edited_at ?? null,
  };
}

/** Unwraps a write envelope, mapping its `result` with `map`. */
export function toWriteResult<TWire, T>(
  w: WriteEnvelopeApi<TWire>,
  map: (r: TWire) => T,
): WriteResult<T> {
  return {
    result: map(w.result),
    actionId: w.action_id,
    actionLabel: w.action_label,
    draft: w.draft ? { seq: w.draft.seq, draftVersion: w.draft.draft_version } : null,
    checks: w.checks,
  };
}
